'use client'

import { useMutation } from '@apollo/client'
import { Button, EmailPhoneInput, Form } from '@chain-zen/ui'
import { zodResolver } from '@hookform/resolvers/zod'
import React, { useCallback } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'

import { ANALYTICS_EVENTS, ANALYTICS_PAGES } from '../../../../constants/analytics'
import { useAnalytics } from '../../../../contexts/analytics-context'
import { INITIATE_AUTH_OR_SIGNUP } from '../../../../data/mutations/auth'
import { useAuth } from '../../../../hooks/use-auth'
import { usePageView } from '../../../../hooks/use-page-view'
import type { IAuthenticateResponseData } from '../../../../types/auth'
import { isEmail } from '../../../../utils/common'

const formSchema = z.object({
  cleanedEmailPhone: z.string().optional(),
  countryCode: z.string().optional(),
  emailOrPhone: z
    .string()
    .min(1, 'Email or phone number is required')
    .refine(
      (value) => {
        const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i
        const phoneRegex = /^\(\d{3}\) \d{3}-\d{4}$/
        return emailRegex.test(value) || phoneRegex.test(value)
      },
      { message: 'Please enter a valid email or phone number' }
    )
})

type FormValues = z.infer<typeof formSchema>

interface CarrierLoginFormProps {
  onAuthInitiated?: (authDetails: IAuthenticateResponseData) => void
  onError?: (error: string) => void
}

export const CarrierLoginForm = ({ onAuthInitiated, onError }: CarrierLoginFormProps) => {
  const { setUserProperty, captureEvent } = useAnalytics()
  usePageView(ANALYTICS_PAGES.carrierLoginForm)
  const { setAuthUserDetails } = useAuth()
  const [isLoading, setIsLoading] = React.useState(false)

  const form = useForm<FormValues>({
    defaultValues: {
      cleanedEmailPhone: '',
      countryCode: '',
      emailOrPhone: ''
    },
    resolver: zodResolver(formSchema)
  })

  const [initiateAuth] = useMutation(INITIATE_AUTH_OR_SIGNUP, {
    onCompleted: async (data) => {
      if (data?.initiateAuthOrSignup) {
        await setAuthUserDetails(data.initiateAuthOrSignup)
        onAuthInitiated?.(data.initiateAuthOrSignup)
      }
    },
    onError: (error) => {
      const errorMessage = error.message || 'Something went wrong. Please try again.'
      toast.error(errorMessage)
      onError?.(errorMessage)
      console.error('Login error: ', error.message)
      form.reset()
    }
  })

  const onSubmit = useCallback(
    async (data: FormValues) => {
      setIsLoading(true)
      try {
        await initiateAuth({
          variables: {
            createdVia: 'ChaineWebApp',
            emailOrPhone: data.cleanedEmailPhone
          }
        }).then(() => {
          setUserProperty(
            isEmail(data.cleanedEmailPhone ?? '') ? { email: data.emailOrPhone } : { phone: data.emailOrPhone }
          )
          captureEvent(ANALYTICS_EVENTS.auth.emailPhoneSubmit, { fromPage: 'carrier-login-form' })
        })
      } catch (error) {
        console.error('Login error: ', error)
        onError?.('Failed to initiate authentication')
      } finally {
        setIsLoading(false)
      }
    },
    [initiateAuth, setUserProperty, captureEvent, onError]
  )

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6" data-testid="carrier-login-form">
        <EmailPhoneInput
          name="emailOrPhone"
          placeholder="Email or phone number..."
          data-testid="email-phone-input"
          className="max-w-md w-full text-base"
        />
        <Button
          type="submit"
          className="w-full"
          variant="default"
          disabled={isLoading}
          loading={isLoading}
          data-testid="submit-button"
        >
          {isLoading ? 'Processing...' : 'Continue'}
        </Button>
      </form>
    </Form>
  )
}
